import {
  choosePath,
  formatMoney,
  formatRange,
  formatWhole,
  getComparison,
  getPlan,
  keepComparison,
  keptLegs,
  NotFound,
  Pick,
  type Comparison,
  type KeptLeg,
  type Money,
  type Plan,
  type PlanStop,
  type RoutePath,
} from '@trippath/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { day } from '../../lib/format';
import { pairs, pathsMap } from '../../lib/map';
import { MapPanel } from '../../components/map/MapPanel';
import { useLive } from '../../app/live';
import { useReadOnly } from '../../app/readOnly';

/** A city's airport, by the city it serves, as the comparison names its ends. */
const serves = (airport: { city: string }, city: string) =>
  airport.city.toLowerCase() === city.toLowerCase();

/**
 * Why the flights read make no path, in words: none leaves the start, none reaches the end, one
 * flies into the start (it belongs to the leg before), or they do not join up.
 */
export function whyNone(
  compared: Comparison,
): { key: string; values: Record<string, string> }[] {
  const { from, to, flights } = compared;
  const read = flights
    .map((f) => [f.from, ...f.via, f.to].map((a) => a.code).join(' → '))
    .join(', ');
  const values = { from, to, flights: read };
  const why: { key: string; values: Record<string, string> }[] = [];
  if (flights.length === 0) return [{ key: 'noneRead', values }];
  if (!flights.some((f) => serves(f.from, from))) why.push({ key: 'noneFrom', values });
  if (!flights.some((f) => serves(f.to, to))) why.push({ key: 'noneTo', values });
  if (flights.some((f) => serves(f.to, from))) why.push({ key: 'intoStart', values });
  if (why.length === 0) why.push({ key: 'noJoin', values });
  return [...why, { key: 'readFlights', values }];
}

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; kept: KeptLeg[] };

/** A path by its flights: two paths may pass the same airports, one ticket or two. */
const key = (path: RoutePath) => path.flights.join('+');
/** A path by its airports, `PEN-KUL-MNL-NRT`, as the plan keeps the one chosen. */
const route = (path: RoutePath) => path.codes.join('-');

/** The path shown first: the one chosen before, or else the cheapest that wastes no ticket. */
function first(compared: Comparison): string | null {
  const chosen = compared.paths.find((p) => route(p) === compared.chosen);
  const cheapest = compared.paths.find((p) => p.cheapest && !p.skipsPaid);
  const shown = chosen ?? cheapest;
  return shown ? key(shown) : null;
}

/**
 * Compare routes (docs/01 §5 v0.4): the flights and stopover hotels found for one leg, every
 * path through them priced, what each does to the trip, and the one chosen put into the plan.
 */
export function Compare() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    Promise.all([getPlan(id), keptLegs(id)]).then(
      ([plan, kept]) => {
        setState({ kind: 'ready', plan, kept });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id, changed]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { plan, kept } = state;
  const legs = pairs(plan.stops);
  const leg = legs.find(([from]) => from.id === params.get('stop')) ?? legs[0];
  return (
    <div className="routes-page">
      {leg ? (
        <LegComparison
          key={leg[0].id}
          plan={plan}
          kept={kept}
          legs={legs}
          leg={leg}
          onLeg={(stop) => {
            setParams({ stop }, { replace: true });
          }}
          onPlan={(next, nextKept) => {
            setState({ kind: 'ready', plan: next, kept: nextKept });
          }}
        />
      ) : (
        <>
          <h1>{t('routes.title')}</h1>
          <p className="quiet">{t('routes.noLegs')}</p>
        </>
      )}
    </div>
  );
}

interface LegProps {
  plan: Plan;
  kept: KeptLeg[];
  legs: [PlanStop, PlanStop][];
  leg: [PlanStop, PlanStop];
  onLeg: (stop: string) => void;
  onPlan: (plan: Plan, kept: KeptLeg[]) => void;
}

/** One leg's comparison: the options typed, the paths, the one picked. */
function LegComparison({ plan, kept, legs, leg, onLeg, onPlan }: LegProps) {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const [from, next] = leg;
  // The stop the comparison reaches, as it was: a stopover put in does not change it.
  const [to] = useState(next);
  const [compared, setCompared] = useState<Comparison | null>(null);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [showSkipping, setShowSkipping] = useState(true);
  const [note, setNote] = useState<'used' | 'failed' | null>(null);

  useEffect(() => {
    getComparison(plan.trip.id, from.id).then(
      (found) => {
        setCompared(found);
        setText(found.text);
        setPicked(first(found));
      },
      () => {
        setNote('failed');
      },
    );
  }, [plan.trip.id, from.id]);

  // The paths in the order chosen: by what they cost, as found, or by their CO₂.
  const [order, setOrder] = useState<'price' | 'co2'>('price');
  const shown = useMemo(
    () =>
      (compared ? compared.paths.filter((p) => showSkipping || !p.skipsPaid) : []).toSorted(
        (a, b) => (order === 'co2' ? a.co2 - b.co2 : 0),
      ),
    [compared, showSkipping, order],
  );
  const mapData = useMemo(
    () => (compared ? pathsMap(compared, shown, picked) : { points: [], lines: [] }),
    [compared, shown, picked],
  );

  const money = (amount: Money) => formatMoney(amount, lang);
  const change = (amount: Money) => {
    const abs = formatWhole({ ...amount, minor: Math.abs(amount.minor) }, lang);
    if (amount.minor < 0) return t('whatIf.less', { amount: abs });
    if (amount.minor > 0) return t('whatIf.more', { amount: abs });
    return t('whatIf.same');
  };
  const status = (stop: string) => {
    const saved = kept.find((k) => k.stop === stop);
    if (saved?.chosen) return t('routes.chosenShort', { path: saved.chosen });
    if (saved && saved.options > 0) return t('routes.options', { count: saved.options });
    return t('routes.empty');
  };
  const keptWith = (found: Comparison) => [
    ...kept.filter((k) => k.stop !== from.id),
    { stop: from.id, options: found.flights.length, chosen: found.chosen },
  ];
  const keep = (nextText: string, maxLegs: number) => {
    setNote(null);
    keepComparison(plan.trip.id, from.id, nextText, maxLegs).then(
      (found) => {
        setCompared(found);
        setPicked(first(found));
        onPlan(plan, keptWith(found));
      },
      () => {
        setNote('failed');
      },
    );
  };
  const use = (base: Comparison, path: RoutePath) => {
    choosePath(plan.trip.id, from.id, path.flights).then(
      (next) => {
        const now = { ...base, chosen: route(path) };
        setNote('used');
        setCompared(now);
        onPlan(next, keptWith(now));
      },
      () => {
        setNote('failed');
      },
    );
  };
  const choice = compared?.paths.find((p) => key(p) === picked);
  const paid = compared ? compared.flights.filter((f) => f.paid) : [];

  return (
    <>
      <div className="routes-head">
        <div>
          <p className="routes-kicker">{t('routes.title')}</p>
          <h1>
            {from.city} <span aria-hidden="true">→</span>
            <span className="visually-hidden">{t('routes.to')}</span> {to.city}
          </h1>
          <p className="quiet">
            {t('routes.leg', {
              number: legs.indexOf(leg) + 1,
              count: legs.length,
              day: day(from.end, lang),
            })}
            {from.out
              ? ` · ${t('routes.planned', { amount: formatRange(from.out.estimate, lang) })}`
              : ''}
          </p>
        </div>
      </div>

      {note === 'failed' && (
        <p className="form-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}

      {compared && (
        <div className="routes-layout">
          <div className="routes-left">
            {/* A trip read only: the ways found compared, none read in or put on the plan. */}
            {!locked && (
              <section className="panel" aria-labelledby={`${ids}-options`}>
                <h2 id={`${ids}-options`}>{t('routes.found')}</h2>
                <label className="visually-hidden" htmlFor={`${ids}-text`}>
                  {t('routes.textLabel')}
                </label>
                <textarea
                  id={`${ids}-text`}
                  className="text-input options-text"
                  rows={11}
                  spellCheck={false}
                  value={text}
                  aria-describedby={`${ids}-hint`}
                  onChange={(event) => {
                    setText(event.target.value);
                  }}
                />
                <p className="quiet routes-hint" id={`${ids}-hint`}>
                  {t('routes.hint')}
                </p>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => {
                    keep(text, compared.maxLegs);
                  }}
                >
                  {t('routes.read')}
                </button>
              </section>
            )}
            <section className="panel" aria-labelledby={`${ids}-read`}>
              <h2 id={`${ids}-read`}>{t('routes.readTitle')}</h2>
              <dl className="read-summary">
                <div>
                  <dt>{t('routes.flights')}</dt>
                  <dd>{compared.flights.length}</dd>
                </div>
                <div>
                  <dt>{t('routes.hotels')}</dt>
                  <dd>{compared.hotels.length}</dd>
                </div>
                <div>
                  <dt>{t('routes.paid')}</dt>
                  <dd className={paid.length === 0 ? undefined : 'paid'}>
                    {paid.length === 0
                      ? t('routes.nonePaid')
                      : paid.map((f) => `${f.from.code} → ${f.to.code}`).join(', ')}
                  </dd>
                </div>
                <div>
                  <dt>{t('routes.pathsFound')}</dt>
                  <dd>{compared.paths.length}</dd>
                </div>
              </dl>
              {compared.problems.length > 0 && (
                <ul className="problems" aria-label={t('routes.notRead')}>
                  {compared.problems.map((problem) => (
                    <li key={problem.line}>
                      {t(problem.problem === 'code' ? 'routes.unknownCode' : 'routes.unread', {
                        line: problem.line,
                        text: problem.text,
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="routes-middle">
            {mapData.points.length > 0 && (
              <section className="panel routes-map" aria-label={t('map.title')}>
                <MapPanel
                  data={mapData}
                  label={t('map.label', {
                    places: mapData.points.map((p) => p.label).join(', '),
                  })}
                />
              </section>
            )}
            <section aria-labelledby={`${ids}-paths`}>
              <h2 id={`${ids}-paths`} className="routes-paths-title">
                {t('routes.paths', { count: compared.paths.length })}
              </h2>
              <div className="routes-controls">
                <div className="routes-changes">
                  <Pick
                    label={t('routes.changes')}
                    options={[1, 2, 3, 4].map((flights) => ({
                      value: String(flights),
                      label: t('routes.changesUpTo', { count: flights - 1 }),
                    }))}
                    value={String(compared.maxLegs)}
                    searchable={false}
                    onChange={(flights) => {
                      keep(text, Number(flights));
                    }}
                  />
                </div>
                {paid.length > 0 && (
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={showSkipping}
                      onChange={(event) => {
                        setShowSkipping(event.target.checked);
                      }}
                    />
                    {t('routes.showSkipping')}
                  </label>
                )}
              </div>
              {compared.paths.length === 0 &&
                (compared.text.trim() === '' ? (
                  <p className="quiet">{t('routes.start')}</p>
                ) : (
                  <div className="routes-why" role="status">
                    {whyNone(compared).map((line) => (
                      <p key={line.key}>{t(`routes.${line.key}`, line.values)}</p>
                    ))}
                  </div>
                ))}
              {shown.length > 1 && (
                <div
                  className="segmented segmented-small routes-order"
                  role="group"
                  aria-label={t('routes.order')}
                >
                  {(['price', 'co2'] as const).map((by) => (
                    <button
                      key={by}
                      type="button"
                      aria-pressed={order === by}
                      onClick={() => {
                        setOrder(by);
                      }}
                    >
                      {t(by === 'price' ? 'routes.byPrice' : 'routes.byCo2')}
                    </button>
                  ))}
                </div>
              )}
              <ul className="path-list" aria-labelledby={`${ids}-paths`}>
                {shown.map((path) => (
                  <li key={key(path)}>
                    <button
                      type="button"
                      className="path"
                      aria-pressed={key(path) === picked}
                      disabled={path.skipsPaid}
                      onClick={() => {
                        setPicked(key(path));
                      }}
                    >
                      <span className="path-head">
                        <span className="path-route">{path.codes.join(' → ')}</span>
                        <span className="path-tags">
                          {path.cheapest && <span className="tag">{t('routes.cheapest')}</span>}
                          {path.fewest && <span className="tag">{t('routes.fewest')}</span>}
                          {path.flights.some((at) => compared.flights[at]?.link) && (
                            <span className="tag">{t('routes.fromLink')}</span>
                          )}
                          {path.skipsPaid && (
                            <span className="tag tag-skips">{t('routes.skipsPaid')}</span>
                          )}
                        </span>
                      </span>
                      <span className="path-breakdown">
                        {[
                          t('routes.breakdown', {
                            flights: money(path.flightsCost),
                            count: path.flights.length,
                          }),
                          ...path.stopovers.map((s) =>
                            t('routes.stopover', {
                              city: s.at.city,
                              cost: money(s.cost),
                              count: s.nights,
                            }),
                          ),
                          ...(path.lost.minor > 0
                            ? [t('routes.lost', { amount: money(path.lost) })]
                            : []),
                        ].join(' · ')}
                      </span>
                      <span className="path-foot">
                        <span className="path-co2">
                          {t('routes.co2', { kg: Math.round(path.co2).toLocaleString(lang) })}
                        </span>
                        <span className="quiet">{t('routes.total')}</span>
                        <span className="path-total">{money(path.total)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="routes-right">
            {choice && (
              <section className="budget-card chosen-path" aria-labelledby={`${ids}-choice`}>
                <h2 id={`${ids}-choice`}>{t('routes.ifPickedTitle')}</h2>
                <p className="chosen-route">{choice.codes.join(' → ')}</p>
                <dl className="so-far">
                  <div>
                    <dt>{t('routes.thisLeg')}</dt>
                    <dd>{money(choice.total)}</dd>
                  </div>
                  <div>
                    <dt>{t('routes.nightsAt', { city: to.city })}</dt>
                    <dd>
                      {t('routes.nightsOf', {
                        left: Math.max(0, to.days - choice.nights),
                        of: to.days,
                      })}
                    </dd>
                  </div>
                  <div className="so-far-day">
                    <dt>{t('routes.tripExpected')}</dt>
                    <dd>
                      {formatWhole(
                        {
                          ...plan.totals.expected,
                          minor: plan.totals.expected.minor + choice.change.minor,
                        },
                        lang,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>{t('routes.versusPlan')}</dt>
                    <dd className={choice.change.minor < 0 ? 'paid' : undefined}>
                      {change(choice.change)}
                    </dd>
                  </div>
                </dl>
                {choice.flights.map((at) => {
                  const link = compared.flights[at]?.link;
                  return (
                    link && (
                      <p key={at} className="chosen-link">
                        <a href={link} target="_blank" rel="noreferrer">
                          {t('routes.openLink')}
                        </a>
                      </p>
                    )
                  );
                })}
                {compared.chosen === route(choice) ? (
                  <p className="chosen-in">{t('routes.inPlan')}</p>
                ) : locked ? null : (
                  <button
                    type="button"
                    className="button chosen-use"
                    onClick={() => {
                      use(compared, choice);
                    }}
                  >
                    {t('routes.use')}
                  </button>
                )}
                {note === 'used' && (
                  <p className="added" role="status">
                    {t('routes.used')}{' '}
                    <Link to={`/trips/${plan.trip.id}`}>{t('search.toPlan')}</Link>
                  </p>
                )}
              </section>
            )}
            <section className="panel" aria-labelledby={`${ids}-how`}>
              <h2 id={`${ids}-how`}>{t('routes.howTitle')}</h2>
              <p className="quiet routes-how">{t('routes.how', { to: compared.to })}</p>
            </section>
            <section className="panel saved-legs" aria-labelledby={`${ids}-saved`}>
              <h2 id={`${ids}-saved`}>{t('routes.saved')}</h2>
              <ul>
                {legs.map(([a, b]) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      aria-current={a.id === from.id ? 'true' : undefined}
                      onClick={() => {
                        onLeg(a.id);
                      }}
                    >
                      <span>
                        {a.city} → {b.city}
                      </span>
                      <span className="quiet">{status(a.id)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </>
  );
}
